import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './caseManagement.css';
import BASE_URL from "../../config";
import CaseLogsPanel from "../../components/CaseLogsPanel";

const CaseManagement = () => {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connectedDeviceSerials, setConnectedDeviceSerials] = useState({});
    const [filters, setFilters] = useState({
        serial: '',
        mac: '',
        uid: '',
        connected_device_uid: '',
        connected_serial: '',
        batch_name: '',
        device_key: '',
        status: '',
        current_version: '',
        apn: '',
        iccid: '',
        details: '',
        log_auto_report: '',
    });

    const [activeSearchKey, setActiveSearchKey] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [logsCase, setLogsCase] = useState(null);
    const [addCaseForm, setAddCaseForm] = useState({
        mac: '',
        batch: '',
        status: 'PENDING',
        connected_device_uid: '',
        apn: 'eapn1.net',
        details: '',
    });
    const [addCaseLoading, setAddCaseLoading] = useState(false);
    const [addCaseError, setAddCaseError] = useState(null);
    const [addCaseSuccess, setAddCaseSuccess] = useState(false);
    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    // Edit case state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editCaseForm, setEditCaseForm] = useState({
        uid: '',
        mac: '',
        serial: '',
        batch: '',
        status: '',
        current_version: '',
        apn: '',
        iccid: '',
        connected_device_uid: '',
        details: '',
        device_key: '',
        log_auto_report: false,
    });
    const [editCaseLoading, setEditCaseLoading] = useState(false);
    const [editCaseError, setEditCaseError] = useState(null);
    const [editCaseSuccess, setEditCaseSuccess] = useState(false);

    // Device search state for Add form
    const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
    const [deviceSearchResults, setDeviceSearchResults] = useState([]);
    const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
    const [deviceSearchLoading, setDeviceSearchLoading] = useState(false);

    // Device search state for Edit form
    const [editDeviceSearchQuery, setEditDeviceSearchQuery] = useState('');
    const [editDeviceSearchResults, setEditDeviceSearchResults] = useState([]);
    const [showEditDeviceDropdown, setShowEditDeviceDropdown] = useState(false);
    const [editDeviceSearchLoading, setEditDeviceSearchLoading] = useState(false);

    // Device serial search state for Add form
    const [deviceSerialQuery, setDeviceSerialQuery] = useState('');
    const [deviceSerialResults, setDeviceSerialResults] = useState([]);
    const [showSerialDropdown, setShowSerialDropdown] = useState(false);
    const [serialSearchLoading, setSerialSearchLoading] = useState(false);

    // Device serial search state for Edit form
    const [editDeviceSerialQuery, setEditDeviceSerialQuery] = useState('');
    const [editDeviceSerialResults, setEditDeviceSerialResults] = useState([]);
    const [showEditSerialDropdown, setShowEditSerialDropdown] = useState(false);
    const [editSerialSearchLoading, setEditSerialSearchLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('jwt_token');

        // Fetch cases
        fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch cases');
                }
                return response.json();
            })
            .then(async (data) => {
                setCases(data);

                // Fetch all devices to get the connected device serials
                try {
                    const devicesResponse = await fetch(`${BASE_URL}/api/devices/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                    });

                    if (devicesResponse.ok) {
                        const devices = await devicesResponse.json();

                        // Create a mapping of UID to serial
                        const serialsMap = {};
                        devices.forEach(device => {
                            if (device.uid && device.serial) {
                                serialsMap[device.uid] = device.serial;
                            }
                        });

                        setConnectedDeviceSerials(serialsMap);
                    }
                } catch (err) {
                    console.error('Error fetching device serials:', err);
                }

                setLoading(false);
            })
            .catch((err) => {
                setError('Error fetching cases');
                setLoading(false);
            });

        // Fetch batches
        setBatchesLoading(true);
        fetch(`${BASE_URL}/api/pocket/admin/batches/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to fetch batches');
                }
                return response.json();
            })
            .then((data) => {
                setBatches(data);
                setBatchesLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching batches:', err);
                setBatchesLoading(false);
            });
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            const isDropdownClick = target.closest('.searchable-dropdown-container');

            if (!isDropdownClick) {
                setShowDeviceDropdown(false);
                setShowEditDeviceDropdown(false);
                setShowSerialDropdown(false);
                setShowEditSerialDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleFilterChange = (e, key) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [key]: e.target.value
        }));
    };

    const toggleSearchInput = (key) => {
        setActiveSearchKey(prevKey => (prevKey === key ? null : key));
    };

    const filteredCases = cases.filter((caseItem) =>
        Object.entries(filters).every(([key, value]) => {
            if (!value) return true;

            // Special handling for connected_serial filter
            if (key === 'connected_serial') {
                const connectedSerial = connectedDeviceSerials[caseItem.connected_device_uid] || "";
                return connectedSerial.toString().toLowerCase().includes(value.toLowerCase());
            }

            const fieldValue = caseItem[key] ?? "";
            return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
        })
    );

    const fetchCases = async () => {
        const token = localStorage.getItem('jwt_token');

        try {
            // Fetch cases
            const casesResponse = await fetch(`${BASE_URL}/api/pocket/admin/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!casesResponse.ok) {
                throw new Error('Failed to fetch cases');
            }

            const casesData = await casesResponse.json();
            setCases(casesData);

            // Also re-fetch device serials to update the mapping
            const devicesResponse = await fetch(`${BASE_URL}/api/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (devicesResponse.ok) {
                const devices = await devicesResponse.json();

                // Create a mapping of UID to serial
                const serialsMap = {};
                devices.forEach(device => {
                    if (device.uid && device.serial) {
                        serialsMap[device.uid] = device.serial;
                    }
                });

                setConnectedDeviceSerials(serialsMap);
            }

            setLoading(false);
        } catch (err) {
            setError('Error fetching cases');
            setLoading(false);
        }
    };

    const handleAddCaseChange = (e) => {
        const { name, value } = e.target;
        setAddCaseForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddCase = async (e) => {
        e.preventDefault();
        setAddCaseLoading(true);
        setAddCaseError(null);
        setAddCaseSuccess(false);

        const token = localStorage.getItem('jwt_token');

        const formattedData = {
            mac: addCaseForm.mac,
            batch: addCaseForm.batch ? parseInt(addCaseForm.batch) : null,
            status: addCaseForm.status || 'PENDING',
            apn: addCaseForm.apn || 'eapn1.net',
            details: addCaseForm.details || '',
        };

        try {
            const response = await fetch(`${BASE_URL}/api/pocket/admin/devices/add/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formattedData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add case');
            }

            const data = await response.json();
            const createdCaseUid = data.device?.uid; // Backend returns { device: { uid: "..." } }

            // If a device was selected, create a connection using the new API
            if (addCaseForm.connected_device_uid && createdCaseUid) {
                try {
                    const connectionResponse = await fetch(`${BASE_URL}/api/pocket/admin/connections/connect/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            device_uid: addCaseForm.connected_device_uid,
                            case_uid: createdCaseUid
                        }),
                    });

                    if (!connectionResponse.ok) {
                        const errorData = await connectionResponse.json();
                        throw new Error(errorData.message || 'Failed to create connection');
                    }
                } catch (err) {
                    // Show error to user if connection failed
                    setAddCaseError(err.message || 'Error creating connection');
                    setAddCaseLoading(false);
                    return; // Don't show success or close modal
                }
            }

            setAddCaseSuccess(true);
            setAddCaseForm({
                mac: '',
                batch: '',
                status: 'PENDING',
                connected_device_uid: '',
                apn: 'eapn1.net',
                details: '',
            });

            // Refresh the cases list with a small delay to ensure backend has updated
            setTimeout(() => {
                fetchCases();
            }, 300);

            // Close modal after 2 seconds
            setTimeout(() => {
                setShowAddModal(false);
                setAddCaseSuccess(false);
            }, 2000);

        } catch (err) {
            setAddCaseError(err.message || 'Error adding case');
        } finally {
            setAddCaseLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setAddCaseForm({
            mac: '',
            batch: '',
            status: 'PENDING',
            connected_device_uid: '',
            apn: 'eapn1.net',
            details: '',
        });
        setAddCaseError(null);
        setAddCaseSuccess(false);
        setDeviceSearchQuery('');
        setDeviceSearchResults([]);
        setShowDeviceDropdown(false);
        setDeviceSerialQuery('');
        setDeviceSerialResults([]);
        setShowSerialDropdown(false);
    };

    // Device search functionality for Add form
    const handleDeviceSearch = async (query) => {
        setDeviceSearchQuery(query);

        if (!query || query.trim().length < 2) {
            setDeviceSearchResults([]);
            setShowDeviceDropdown(false);
            return;
        }

        setDeviceSearchLoading(true);
        setShowDeviceDropdown(true);

        const token = localStorage.getItem('jwt_token');

        try {
            // Search through all devices and filter by UID
            const response = await fetch(`${BASE_URL}/api/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to search devices');
            }

            const devices = await response.json();

            // Filter devices by UID matching the query
            const filtered = devices.filter(device =>
                device.uid && device.uid.toLowerCase().includes(query.toLowerCase())
            ).slice(0, 10); // Limit to 10 results

            setDeviceSearchResults(filtered);
        } catch (err) {
            console.error('Device search error:', err);
            setDeviceSearchResults([]);
        } finally {
            setDeviceSearchLoading(false);
        }
    };

    const handleDeviceSelect = (device) => {
        setAddCaseForm(prev => ({
            ...prev,
            connected_device_uid: device.uid
        }));
        setDeviceSearchQuery(device.uid);
        setShowDeviceDropdown(false);
    };

    // Device serial search functionality for Add form
    const handleDeviceSerialSearch = async (query) => {
        setDeviceSerialQuery(query);

        if (!query || query.trim().length < 2) {
            setDeviceSerialResults([]);
            setShowSerialDropdown(false);
            return;
        }

        setSerialSearchLoading(true);
        setShowSerialDropdown(true);

        const token = localStorage.getItem('jwt_token');

        try {
            // Search through all devices and filter by Serial
            const response = await fetch(`${BASE_URL}/api/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to search devices');
            }

            const devices = await response.json();

            // Filter devices by Serial matching the query
            const filtered = devices.filter(device =>
                device.serial && device.serial.toLowerCase().includes(query.toLowerCase())
            ).slice(0, 10); // Limit to 10 results

            setDeviceSerialResults(filtered);
        } catch (err) {
            console.error('Device serial search error:', err);
            setDeviceSerialResults([]);
        } finally {
            setSerialSearchLoading(false);
        }
    };

    const handleDeviceSerialSelect = (device) => {
        // Auto-populate both serial and UID
        setAddCaseForm(prev => ({
            ...prev,
            connected_device_uid: device.uid
        }));
        setDeviceSerialQuery(device.serial);
        setDeviceSearchQuery(device.uid); // Also update UID field
        setShowSerialDropdown(false);
    };

    // Edit case handlers
    const handleEditClick = async (caseItem) => {
        // Find the batch ID from batch_name if batch is not provided
        let batchId = '';
        if (caseItem.batch !== null && caseItem.batch !== undefined) {
            batchId = String(caseItem.batch);
        } else if (caseItem.batch_name) {
            // Find batch by name
            const matchingBatch = batches.find(b => b.batch_name === caseItem.batch_name);
            if (matchingBatch) {
                batchId = String(matchingBatch.id);
            }
        }

        setEditCaseForm({
            uid: caseItem.uid,
            mac: caseItem.mac || '',
            serial: caseItem.serial || '',
            batch: batchId,
            status: caseItem.status || 'PENDING',
            current_version: caseItem.current_version ?? '',
            apn: caseItem.apn || 'eapn1.net',
            iccid: caseItem.iccid || '',
            connected_device_uid: caseItem.connected_device_uid || '',
            details: caseItem.details || '',
            device_key: caseItem.device_key || '',
            log_auto_report: !!caseItem.log_auto_report,
        });
        setEditDeviceSearchQuery(caseItem.connected_device_uid || '');

        // Fetch and populate the connected device serial if connected_device_uid exists
        if (caseItem.connected_device_uid) {
            const token = localStorage.getItem('jwt_token');
            try {
                const response = await fetch(`${BASE_URL}/api/devices/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const devices = await response.json();
                    const connectedDevice = devices.find(device => device.uid === caseItem.connected_device_uid);

                    if (connectedDevice) {
                        setEditDeviceSerialQuery(connectedDevice.serial || '');
                    }
                }
            } catch (err) {
                console.error('Error fetching connected device:', err);
            }
        }

        setShowEditModal(true);
    };

    const handleEditCaseChange = (e) => {
        const { name, value } = e.target;
        setEditCaseForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditCaseToggle = (e) => {
        const { name, checked } = e.target;
        setEditCaseForm(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleUpdateCase = async (e) => {
        e.preventDefault();
        setEditCaseLoading(true);
        setEditCaseError(null);
        setEditCaseSuccess(false);

        const token = localStorage.getItem('jwt_token');

        const formattedData = {
            mac: editCaseForm.mac,
            serial: editCaseForm.serial,
            batch: editCaseForm.batch && parseInt(editCaseForm.batch) > 0
                ? parseInt(editCaseForm.batch)
                : null,
            status: editCaseForm.status,
            current_version: editCaseForm.current_version,
            apn: editCaseForm.apn,
            details: editCaseForm.details,
            device_key: editCaseForm.device_key,
            log_auto_report: editCaseForm.log_auto_report,
        };

        try {
            const response = await fetch(`${BASE_URL}/api/pocket/admin/devices/update/${editCaseForm.uid}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formattedData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update case');
            }

            const data = await response.json();

            // Handle connection changes
            // Find the original case data to check if connection changed
            const originalCase = cases.find(c => c.uid === editCaseForm.uid);
            const oldDeviceUid = originalCase?.connected_device_uid;
            const newDeviceUid = editCaseForm.connected_device_uid;

            // If connection changed, handle disconnect/reconnect
            if (oldDeviceUid !== newDeviceUid) {
                try {
                    // First, disconnect old connection if it exists
                    if (oldDeviceUid) {
                        const disconnectResponse = await fetch(`${BASE_URL}/api/pocket/admin/connections/disconnect/`, {
                            method: 'DELETE',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                case_uid: editCaseForm.uid
                            }),
                        });

                        if (!disconnectResponse.ok) {
                            console.warn('Failed to disconnect old connection');
                        }
                    }

                    // Then, create new connection if a device is selected
                    if (newDeviceUid) {
                        const connectionResponse = await fetch(`${BASE_URL}/api/pocket/admin/connections/connect/`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                device_uid: newDeviceUid,
                                case_uid: editCaseForm.uid
                            }),
                        });

                        if (!connectionResponse.ok) {
                            const errorData = await connectionResponse.json();
                            throw new Error(errorData.message || 'Failed to create connection');
                        }
                    }
                } catch (err) {
                    // Show error to user if connection failed
                    setEditCaseError(err.message || 'Error updating connection');
                    setEditCaseLoading(false);
                    return; // Don't show success or close modal
                }
            }

            setEditCaseSuccess(true);

            // Refresh the cases list with a small delay to ensure backend has processed
            setTimeout(() => {
                fetchCases();
            }, 200);

            // Close modal after 2 seconds
            setTimeout(() => {
                setShowEditModal(false);
                setEditCaseSuccess(false);
            }, 2000);

        } catch (err) {
            setEditCaseError(err.message || 'Error updating case');
        } finally {
            setEditCaseLoading(false);
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditCaseForm({
            uid: '',
            mac: '',
            serial: '',
            batch: '',
            status: '',
            current_version: '',
            apn: '',
            iccid: '',
            connected_device_uid: '',
            details: '',
            device_key: '',
            log_auto_report: false,
        });
        setEditCaseError(null);
        setEditCaseSuccess(false);
        setEditDeviceSearchQuery('');
        setEditDeviceSearchResults([]);
        setShowEditDeviceDropdown(false);
        setEditDeviceSerialQuery('');
        setEditDeviceSerialResults([]);
        setShowEditSerialDropdown(false);
    };

    // Device search functionality for Edit form
    const handleEditDeviceSearch = async (query) => {
        setEditDeviceSearchQuery(query);

        if (!query || query.trim().length < 2) {
            setEditDeviceSearchResults([]);
            setShowEditDeviceDropdown(false);
            return;
        }

        setEditDeviceSearchLoading(true);
        setShowEditDeviceDropdown(true);

        const token = localStorage.getItem('jwt_token');

        try {
            // Search through all devices and filter by UID
            const response = await fetch(`${BASE_URL}/api/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to search devices');
            }

            const devices = await response.json();

            // Filter devices by UID matching the query
            const filtered = devices.filter(device =>
                device.uid && device.uid.toLowerCase().includes(query.toLowerCase())
            ).slice(0, 10); // Limit to 10 results

            setEditDeviceSearchResults(filtered);
        } catch (err) {
            console.error('Device search error:', err);
            setEditDeviceSearchResults([]);
        } finally {
            setEditDeviceSearchLoading(false);
        }
    };

    const handleEditDeviceSelect = (device) => {
        setEditCaseForm(prev => ({
            ...prev,
            connected_device_uid: device.uid
        }));
        setEditDeviceSearchQuery(device.uid);
        setShowEditDeviceDropdown(false);
    };

    // Device serial search functionality for Edit form
    const handleEditDeviceSerialSearch = async (query) => {
        setEditDeviceSerialQuery(query);

        if (!query || query.trim().length < 2) {
            setEditDeviceSerialResults([]);
            setShowEditSerialDropdown(false);
            return;
        }

        setEditSerialSearchLoading(true);
        setShowEditSerialDropdown(true);

        const token = localStorage.getItem('jwt_token');

        try {
            // Search through all devices and filter by Serial
            const response = await fetch(`${BASE_URL}/api/devices/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to search devices');
            }

            const devices = await response.json();

            // Filter devices by Serial matching the query
            const filtered = devices.filter(device =>
                device.serial && device.serial.toLowerCase().includes(query.toLowerCase())
            ).slice(0, 10); // Limit to 10 results

            setEditDeviceSerialResults(filtered);
        } catch (err) {
            console.error('Device serial search error:', err);
            setEditDeviceSerialResults([]);
        } finally {
            setEditSerialSearchLoading(false);
        }
    };

    const handleEditDeviceSerialSelect = (device) => {
        // Auto-populate both serial and UID
        setEditCaseForm(prev => ({
            ...prev,
            connected_device_uid: device.uid
        }));
        setEditDeviceSerialQuery(device.serial);
        setEditDeviceSearchQuery(device.uid); // Also update UID field
        setShowEditSerialDropdown(false);
    };

    // Disconnect handler for edit form
    const handleDisconnectDevice = () => {
        const confirmDisconnect = window.confirm(
            'Are you sure you want to disconnect this device from the case?\n\nThis will remove the connection but not delete the device or case.'
        );

        if (confirmDisconnect) {
            setEditCaseForm(prev => ({
                ...prev,
                connected_device_uid: ''
            }));
            setEditDeviceSerialQuery('');
            setEditDeviceSearchQuery('');
        }
    };

    // Delete case handler
    const handleDeleteCase = async (caseItem) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete this case?\n\nUID: ${caseItem.uid}\nSerial: ${caseItem.serial}\nMAC: ${caseItem.mac}\n\nThis action cannot be undone.`
        );

        if (!confirmDelete) {
            return;
        }

        const token = localStorage.getItem('jwt_token');

        try {
            const response = await fetch(`${BASE_URL}/api/pocket/admin/devices/delete/${caseItem.uid}/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete case');
            }

            const data = await response.json();
            alert(`Case deleted successfully!\n\nUID: ${data.deleted_device?.uid || caseItem.uid}\nSerial: ${data.deleted_device?.serial || caseItem.serial}`);

            // Refresh the cases list
            fetchCases();

        } catch (err) {
            alert(`Error: ${err.message || 'Failed to delete case'}`);
            console.error('Delete case error:', err);
        }
    };

    if (loading) {
        return (
            <div className="case-mgmt-dashboard">
                <div className="case-mgmt-loading">
                    <div className="case-mgmt-spinner"></div>
                    <p>Loading case data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="case-mgmt-dashboard">
                <div className="case-mgmt-error">
                    <div className="error-icon">⚠️</div>
                    <h3>Error</h3>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="case-mgmt-dashboard">
            {logsCase && (
                <div className="modal-overlay" onClick={() => setLogsCase(null)}>
                    <div className="modal-content" style={{ maxWidth: 900, width: '90%', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Logs – {logsCase.serial}</h2>
                            <button className="modal-close" onClick={() => setLogsCase(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <CaseLogsPanel caseUid={logsCase.uid} />
                        </div>
                    </div>
                </div>
            )}

            {/* Add Case Modal */}
            {showAddModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add New Case</h2>
                            <button className="modal-close" onClick={handleCloseModal}>✕</button>
                        </div>

                        <form onSubmit={handleAddCase}>
                            <div className="modal-body">
                                {addCaseError && (
                                    <div className="alert alert-error">
                                        <span className="alert-icon">⚠️</span>
                                        {addCaseError}
                                    </div>
                                )}

                                {addCaseSuccess && (
                                    <div className="alert alert-success">
                                        <span className="alert-icon">✓</span>
                                        Case added successfully!
                                    </div>
                                )}
                                

                                <div className="form-section">
                                    <h3 className="form-section-title">Required Information</h3>

                                    <div className="form-group">
                                        <label htmlFor="mac">MAC Address *</label>
                                        <input
                                            type="text"
                                            id="mac"
                                            name="mac"
                                            value={addCaseForm.mac}
                                            onChange={handleAddCaseChange}
                                            placeholder="AA:BB:CC:DD:EE:FF"
                                            required
                                            className="form-input"
                                        />
                                        <span className="form-hint">Format: XX:XX:XX:XX:XX:XX</span>
                                    </div>

                                </div>

                                <div className="form-section">
                                    <h3 className="form-section-title">Optional Information</h3>

                                    <div className="form-group">
                                        <label htmlFor="batch">Batch Name</label>
                                        <select
                                            id="batch"
                                            name="batch"
                                            value={addCaseForm.batch}
                                            onChange={handleAddCaseChange}
                                            className="form-input"
                                            disabled={batchesLoading}
                                        >
                                            <option value="">-- Select Batch --</option>
                                            {batches.map((batch) => (
                                                <option key={batch.id} value={batch.id}>
                                                    {batch.batch_name}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="form-hint">
                                            {batchesLoading ? 'Loading batches...' : 'Select a batch name for this case'}
                                        </span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="status">Status</label>
                                        <select
                                            id="status"
                                            name="status"
                                            value={addCaseForm.status}
                                            onChange={handleAddCaseChange}
                                            className="form-input"
                                        >
                                            <option value="PENDING">Pending</option>
                                            <option value="ACTIVE">Active</option>
                                            <option value="INACTIVE">Inactive</option>
                                        </select>
                                        <span className="form-hint">Case operational status</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="apn">APN</label>
                                        <input
                                            type="text"
                                            id="apn"
                                            name="apn"
                                            value={addCaseForm.apn}
                                            onChange={handleAddCaseChange}
                                            placeholder="eapn1.net"
                                            className="form-input"
                                        />
                                        <span className="form-hint">Cellular APN for this case</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="device_serial">Connected Device Serial</label>
                                        <div className="searchable-dropdown-container">
                                            <input
                                                type="text"
                                                id="device_serial"
                                                name="device_serial"
                                                value={deviceSerialQuery}
                                                onChange={(e) => handleDeviceSerialSearch(e.target.value)}
                                                onFocus={() => {
                                                    if (deviceSerialQuery.length >= 2) {
                                                        setShowSerialDropdown(true);
                                                    }
                                                }}
                                                placeholder="Type to search device serial..."
                                                className="form-input"
                                                autoComplete="off"
                                            />
                                            {serialSearchLoading && (
                                                <div className="search-loading">Searching...</div>
                                            )}
                                            {showSerialDropdown && deviceSerialResults.length > 0 && (
                                                <div className="search-dropdown">
                                                    {deviceSerialResults.map((device) => (
                                                        <div
                                                            key={device.id}
                                                            className="search-dropdown-item"
                                                            onClick={() => handleDeviceSerialSelect(device)}
                                                        >
                                                            <div className="device-uid">{device.serial}</div>
                                                            <div className="device-details">
                                                                UID: {device.uid} | MAC: {device.mac}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {showSerialDropdown && !serialSearchLoading && deviceSerialResults.length === 0 && deviceSerialQuery.length >= 2 && (
                                                <div className="search-dropdown">
                                                    <div className="search-dropdown-empty">No devices found</div>
                                                </div>
                                            )}
                                        </div>
                                        <span className="form-hint">Search by serial</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="connected_device_uid">Connected Device UID (Auto-filled)</label>
                                        <input
                                            type="text"
                                            id="connected_device_uid"
                                            name="connected_device_uid"
                                            value={addCaseForm.connected_device_uid}
                                            className="form-input"
                                            placeholder="Auto-filled from serial search"
                                            readOnly
                                            disabled
                                        />
                                        <span className="form-hint">This field is automatically filled when you select a device by serial</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="details">Details</label>
                                        <textarea
                                            id="details"
                                            name="details"
                                            value={addCaseForm.details}
                                            onChange={handleAddCaseChange}
                                            placeholder="Enter additional details or notes"
                                            rows="3"
                                            className="form-input"
                                        />
                                        <span className="form-hint">Additional notes or information</span>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseModal}
                                    disabled={addCaseLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={addCaseLoading}
                                >
                                    {addCaseLoading ? 'Adding...' : 'Add Case'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Case Modal */}
            {showEditModal && (
                <div className="modal-overlay edit-modal-overlay" onClick={handleCloseEditModal}>
                    <div className="modal-content edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header edit-modal-header">
                            <h2>Edit Case</h2>
                            <button className="modal-close" onClick={handleCloseEditModal}>✕</button>
                        </div>

                        <form onSubmit={handleUpdateCase}>
                            <div className="modal-body edit-modal-body">
                                {editCaseError && (
                                    <div className="alert alert-error">
                                        <span className="alert-icon">⚠️</span>
                                        {editCaseError}
                                    </div>
                                )}

                                {editCaseSuccess && (
                                    <div className="alert alert-success">
                                        <span className="alert-icon">✓</span>
                                        Case updated successfully!
                                    </div>
                                )}

                                <div className="form-section edit-form-section">
                                    <h3 className="form-section-title">Case Information</h3>

                                    <div className="form-group">
                                        <label htmlFor="edit-uid">UID (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-uid"
                                            value={editCaseForm.uid}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-serial">Serial Number (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-serial"
                                            value={editCaseForm.serial}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-mac">MAC Address (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-mac"
                                            value={editCaseForm.mac}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-device_key">Device Key (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-device_key"
                                            value={editCaseForm.device_key}
                                            className="form-input"
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-device_serial">Connected Device Serial</label>
                                        <div className="searchable-dropdown-container">
                                            <input
                                                type="text"
                                                id="edit-device_serial"
                                                name="device_serial"
                                                value={editDeviceSerialQuery}
                                                onChange={(e) => handleEditDeviceSerialSearch(e.target.value)}
                                                onFocus={() => {
                                                    if (editDeviceSerialQuery.length >= 2) {
                                                        setShowEditSerialDropdown(true);
                                                    }
                                                }}
                                                placeholder="Type to search device serial..."
                                                className="form-input"
                                                autoComplete="off"
                                            />
                                            {editSerialSearchLoading && (
                                                <div className="search-loading">Searching...</div>
                                            )}
                                            {showEditSerialDropdown && editDeviceSerialResults.length > 0 && (
                                                <div className="search-dropdown">
                                                    {editDeviceSerialResults.map((device) => (
                                                        <div
                                                            key={device.id}
                                                            className="search-dropdown-item"
                                                            onClick={() => handleEditDeviceSerialSelect(device)}
                                                        >
                                                            <div className="device-uid">{device.serial}</div>
                                                            <div className="device-details">
                                                                UID: {device.uid} | MAC: {device.mac}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {showEditSerialDropdown && !editSerialSearchLoading && editDeviceSerialResults.length === 0 && editDeviceSerialQuery.length >= 2 && (
                                                <div className="search-dropdown">
                                                    <div className="search-dropdown-empty">No devices found</div>
                                                </div>
                                            )}
                                        </div>
                                        <span className="form-hint">Search by serial to auto-fill UID below</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-connected_device_uid">Connected Device UID (Auto-filled)</label>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                id="edit-connected_device_uid"
                                                name="connected_device_uid"
                                                value={editCaseForm.connected_device_uid}
                                                className="form-input"
                                                placeholder="Auto-filled from serial search"
                                                readOnly
                                                disabled
                                                style={{ flex: 1, margin: 0 }}
                                            />
                                            {editCaseForm.connected_device_uid && (
                                                <button
                                                    type="button"
                                                    onClick={handleDisconnectDevice}
                                                    className="btn-delete"
                                                    style={{
                                                        padding: '10px 20px',
                                                        minHeight: '42px',
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 'fit-content'
                                                    }}
                                                    title="Disconnect device"
                                                >
                                                    Disconnect
                                                </button>
                                            )}
                                        </div>
                                        <span className="form-hint">This field is automatically filled when you select a device by serial</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-batch">Batch Name</label>
                                        <select
                                            id="edit-batch"
                                            name="batch"
                                            value={editCaseForm.batch}
                                            onChange={handleEditCaseChange}
                                            className="form-input"
                                            disabled={batchesLoading}
                                        >
                                            <option value="">-- No Batch --</option>
                                            {batches.map((batch) => (
                                                <option key={batch.id} value={batch.id}>
                                                    {batch.batch_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-current_version">Current Version</label>
                                        <input
                                            type="text"
                                            id="edit-current_version"
                                            name="current_version"
                                            value={editCaseForm.current_version}
                                            onChange={handleEditCaseChange}
                                            placeholder="Enter version"
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-apn">APN</label>
                                        <input
                                            type="text"
                                            id="edit-apn"
                                            name="apn"
                                            value={editCaseForm.apn}
                                            onChange={handleEditCaseChange}
                                            placeholder="eapn1.net"
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-iccid">ICCID (Read-only)</label>
                                        <input
                                            type="text"
                                            id="edit-iccid"
                                            value={editCaseForm.iccid || '—'}
                                            className="form-input"
                                            disabled
                                            readOnly
                                        />
                                        <span className="form-hint">Reported by the case device</span>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-details">Details</label>
                                        <textarea
                                            id="edit-details"
                                            name="details"
                                            value={editCaseForm.details}
                                            onChange={handleEditCaseChange}
                                            placeholder="Enter additional details"
                                            rows="3"
                                            className="form-input"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-status">Status</label>
                                        <select
                                            id="edit-status"
                                            name="status"
                                            value={editCaseForm.status}
                                            onChange={handleEditCaseChange}
                                            className="form-input"
                                        >
                                            <option value="PENDING">Pending</option>
                                            <option value="ACTIVE">Active</option>
                                            <option value="INACTIVE">Inactive</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-log_auto_report" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                id="edit-log_auto_report"
                                                name="log_auto_report"
                                                checked={editCaseForm.log_auto_report}
                                                onChange={handleEditCaseToggle}
                                                style={{ width: '16px', height: '16px', margin: 0 }}
                                            />
                                            Auto Log Report
                                        </label>
                                        <span className="form-hint">
                                            When enabled, the case automatically uploads its log files to the server.
                                            Synced to the device twin (desired.case.settings.log_auto_report).
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer edit-modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseEditModal}
                                    disabled={editCaseLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={editCaseLoading}
                                >
                                    {editCaseLoading ? 'Updating...' : 'Update Case'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="case-mgmt-header">
                <div className="header-content">
                    <div className="title-row">
                        <div>
                            <h1 className="case-mgmt-title">Case Management</h1>
                            <p className="case-mgmt-subtitle">View and manage all registered cases</p>
                        </div>
                        <button className="btn-add-case" onClick={() => setShowAddModal(true)}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Case
                        </button>
                    </div>
                </div>
                <div className="header-stats">
                    <div className="stat-card">
                        <div className="stat-label">Total Cases</div>
                        <div className="stat-value total">{cases.length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Active</div>
                        <div className="stat-value active">{cases.filter(c => c.status === 'ACTIVE').length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Pending</div>
                        <div className="stat-value pending">{cases.filter(c => c.status === 'PENDING').length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-label">Inactive</div>
                        <div className="stat-value inactive">{cases.filter(c => c.status === 'INACTIVE').length}</div>
                    </div>
                </div>
            </div>

            <div className="case-mgmt-content">
                <div className="table-container">
                    <div className="table-header">
                        <h2>Cases ({filteredCases.length})</h2>
                        {Object.entries(filters).some(([_, value]) => value !== '') && (
                            <div className="active-filters-container">
                                {Object.entries(filters)
                                    .filter(([_, value]) => value !== '')
                                    .map(([key, value]) => (
                                        <div key={key} className="active-filter-badge">
                                            <strong>{key.replace('_', ' ')}:</strong> {value}
                                            <button
                                                className="clear-filter"
                                                onClick={() => {
                                                    setFilters(prev => ({...prev, [key]: ''}));
                                                    if (activeSearchKey === key) {
                                                        setActiveSearchKey(null);
                                                    }
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                <button
                                    className="clear-all-filters"
                                    onClick={() => {
                                        setFilters({
                                            serial: '',
                                            mac: '',
                                            uid: '',
                                            connected_device_uid: '',
                                            connected_serial: '',
                                            batch_name: '',
                                            device_key: '',
                                            status: '',
                                            current_version: '',
                                            apn: '',
                                            iccid: '',
                                            details: '',
                                            log_auto_report: '',
                                        });
                                        setActiveSearchKey(null);
                                    }}
                                >
                                    Clear All
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="table-wrapper">
                        <table className="case-mgmt-table">
                            <thead>
                                <tr>
                                    {[
                                        { key: 'serial', label: 'Serial' },
                                        { key: 'mac', label: 'MAC Address' },
                                        { key: 'uid', label: 'UID' },
                                        { key: 'connected_device_uid', label: 'Connected UID' },
                                        { key: 'connected_serial', label: 'Connected Serial' },
                                        { key: 'batch_name', label: 'Batch Name' },
                                        { key: 'device_key', label: 'Device Key' },
                                        { key: 'current_version', label: 'Version' },
                                        { key: 'apn', label: 'APN' },
                                        { key: 'iccid', label: 'ICCID' },
                                        { key: 'details', label: 'Details' },
                                        { key: 'status', label: 'Status' },
                                        { key: 'log_auto_report', label: 'Auto Log' },
                                    ].map(({ key, label }) => (
                                        <th key={key}>
                                            <div className="th-content">
                                                <span className="th-label">{label}</span>
                                                <button
                                                    className={`search-toggle ${activeSearchKey === key ? 'active' : ''}`}
                                                    onClick={() => toggleSearchInput(key)}
                                                    title="Search"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="11" cy="11" r="8"></circle>
                                                        <path d="m21 21-4.35-4.35"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                            {activeSearchKey === key && (
                                                <div className="search-input-container">
                                                    <input
                                                        type="text"
                                                        className="search-input"
                                                        placeholder={`Search ${label.toLowerCase()}...`}
                                                        value={filters[key]}
                                                        onChange={(e) => handleFilterChange(e, key)}
                                                        autoFocus
                                                    />
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th>
                                        <div className="th-content">
                                            <span className="th-label">Actions</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCases.length === 0 ? (
                                    <tr>
                                        <td colSpan="14" className="no-results">
                                            <div className="no-results-content">
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <circle cx="11" cy="11" r="8"></circle>
                                                    <path d="m21 21-4.35-4.35"></path>
                                                </svg>
                                                <p>No cases found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCases.map((caseItem) => (
                                        <tr
                                            key={caseItem.id}
                                            className="clickable-row"
                                            onClick={() => handleEditClick(caseItem)}
                                        >
                                            <td className="serial-cell">{caseItem.serial}</td>
                                            <td className="mac-cell">{caseItem.mac}</td>
                                            <td className="uid-cell">{caseItem.uid}</td>
                                            <td className="connected-uid-cell">{caseItem.connected_device_uid || '-'}</td>
                                            <td className="connected-serial-cell">
                                                {caseItem.connected_device_uid ? (connectedDeviceSerials[caseItem.connected_device_uid] || '-') : '-'}
                                            </td>
                                            <td>{caseItem.batch_name || '-'}</td>
                                            <td className="key-cell">{caseItem.device_key}</td>
                                            <td>{caseItem.current_version}</td>
                                            <td>{caseItem.apn || '-'}</td>
                                            <td>{caseItem.iccid || '-'}</td>
                                            <td className="details-cell">{caseItem.details || '-'}</td>
                                            <td>
                                                <span className={`status-badge status-${caseItem.status}`}>
                                                    {caseItem.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge status-${caseItem.log_auto_report ? 'ACTIVE' : 'INACTIVE'}`}>
                                                    {caseItem.log_auto_report ? 'On' : 'Off'}
                                                </span>
                                            </td>
                                            <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="btn-edit"
                                                    onClick={() => setLogsCase(caseItem)}
                                                    title="Case logs"
                                                    style={{ fontSize: 11, fontWeight: 600, marginRight: 6 }}
                                                >
                                                    Logs
                                                </button>
                                                <button
                                                    className="btn-edit"
                                                    onClick={() => handleEditClick(caseItem)}
                                                    title="Edit case"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                                <button
                                                    className="btn-delete"
                                                    onClick={() => handleDeleteCase(caseItem)}
                                                    title="Delete case"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CaseManagement;
